import React from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import { Dropdown } from 'primereact/dropdown'
import { FileUpload, ItemTemplateOptions } from 'primereact/fileupload'
import { classNames } from 'primereact/utils'

import { useSelectFirstAndOnlyOption } from '../../../../../../../hooks'
import { ConfigurableComponent } from '../../../../../../../utils/wrapper'
import { useConfigContext } from '../../../../../../ui-config'
import { FileUploadItemTemplate } from '../template/FileUploadItemTemplate'

type FormFieldName = 'attachment' | 'category'

type Props = {
  formik: FormikProps<any>
  isFormFieldValid: (name: FormFieldName) => boolean
  getFormErrorMessage: (name: FormFieldName) => false | JSX.Element
}

export const SectionForm: React.FC<Props> = ({
  formik,
  isFormFieldValid,
  getFormErrorMessage,
}) => {
  const { t } = useTranslation()
  const { uiConfig: config } = useConfigContext()

  // attachment
  const attachmentLabel = (
    <label
      htmlFor="attachment"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('attachment'),
      })}
    ></label>
  )

  const uploadOptions = {
    icon: '',
    iconOnly: true,
    className: 'hidden',
  }
  const cancelOptions = {
    icon: '',
    iconOnly: true,
    className: 'hidden',
  }

  const onTemplateRemove = (callback: Function) => {
    formik.setFieldValue('attachment', null)
    callback()
  }

  const itemTemplate = (fileObject: any, props: ItemTemplateOptions) => {
    return (
      <FileUploadItemTemplate
        fileObject={fileObject}
        props={props}
        onTemplateRemove={onTemplateRemove}
      />
    )
  }

  const attachmentComponent = (
    <FileUpload
      id="attachment"
      uploadOptions={uploadOptions}
      cancelOptions={cancelOptions}
      itemTemplate={itemTemplate}
      emptyTemplate={t(
        'invoice.attachments.table.upload.dialog.form.attachment.emptyMessage',
      )}
      onRemove={(event: any) => formik.setFieldValue('attachment', null)}
      onSelect={(event: any) =>
        formik.setFieldValue('attachment', event.files[0])
      }
      invalidFileSizeMessageSummary={t(
        'invoice.attachments.table.upload.dialog.form.attachment.validation.invalidFileSize.summary',
      )}
      invalidFileSizeMessageDetail={t(
        'invoice.attachments.table.upload.dialog.form.attachment.validation.invalidFileSize.detail',
      )}
    />
  )

  const attachmentErrorMsg = <div>{getFormErrorMessage('attachment')}</div>

  const attachmentWrapper = (
    <ConfigurableComponent
      config={config.invoice.uploadAttachment}
      component={attachmentComponent}
      label={attachmentLabel}
      errorMsg={attachmentErrorMsg}
    />
  )

  // category
  const categoryLabel = (
    <label
      htmlFor="category"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('category'),
      })}
    ></label>
  )

  useSelectFirstAndOnlyOption(
    'category',
    config.invoice.uploadAttachment.category.options,
    formik,
  )

  const categoryComponent = (
    <Dropdown
      id="category"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('category'),
      })}
      value={formik.values.category}
      onChange={formik.handleChange}
      optionLabel="code"
      optionValue="code"
    />
  )

  const categoryErrorMsg = <div>{getFormErrorMessage('category')}</div>

  const categoryWrapper = (
    <ConfigurableComponent
      config={config.invoice.uploadAttachment}
      component={categoryComponent}
      label={categoryLabel}
      errorMsg={categoryErrorMsg}
    />
  )
  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="formgrid grid">
        <div className="col">{attachmentWrapper}</div>
      </div>

      <div className="formgrid grid">
        <div className="col">{categoryWrapper}</div>
      </div>
    </form>
  )
}
